import React, { useEffect, useState, useRef } from "react";
import "./LandingPage.css";
import {
  HeroSection,
  VideoSection,
  DesignSection,
  TechSection,
  ServicesSection,
  KioskSection,
  CTASection,
} from "./sections";

// Import images
import laptopImage1 from "../assets/20251022_173912.png";
import laptopImage2 from "../assets/20251022_174017.png";
import laptopImage3 from "../assets/20251022_174155.png";
import laptopImage4 from "../assets/20251022_174203.png";
import clinicImage from "../assets/kiosk.png";
import easyUseImage from "../assets/easy_use.jpg";
import mainHeroVideo from "../assets/animations/main_hero.webm";
import mainHeroFallback from "../assets/chart_hero.png";
import heroColorVideo from "../assets/animations/hero-color.mp4";
import heroAlphaVideo from "../assets/animations/hero-alpha.mp4";
import techImage from "../assets/tech1.png";
import kioskVideo from "../assets/animations/kiosk.webm";
import kioskVideoWebP from "../assets/animations/kiosk.webp";
import kioskColorVideo from "../assets/animations/kiosk-color.mp4";
import kioskAlphaVideo from "../assets/animations/kiosk-alpha.mp4";
import kioskFallback from "../assets/kiosk.png";
import kioskContents from "../assets/kiosk_contents.svg";

const LandingPage: React.FC = () => {
  const heroVideoRef = useRef<HTMLVideoElement>(null);
  const heroSectionRef = useRef<HTMLDivElement>(null);
  const heroAlphaVideoRef = useRef<HTMLVideoElement>(null);
  const kioskVideoRef = useRef<HTMLVideoElement>(null);
  const kioskSectionRef = useRef<HTMLDivElement>(null);

  const [videoPhase, setVideoPhase] = useState<
    "initial" | "forward" | "reverse" | "complete"
  >("initial");
  const [videoDuration, setVideoDuration] = useState(0);
  const [supportsWebM, setSupportsWebM] = useState(true);
  const [isIOS, setIsIOS] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isInIframe, setIsInIframe] = useState(false);
  const [iframePlayCount, setIframePlayCount] = useState(0);

  const laptopImages = [laptopImage1, laptopImage2, laptopImage3, laptopImage4];

  // iOS 감지 (WebM 알파채널 미지원)
  useEffect(() => {
    const ua = navigator.userAgent;

    // iOS 기기 감지 (Safari, Chrome, 기타 모든 iOS 브라우저)
    // iOS에서는 모든 브라우저가 WebKit 엔진을 사용하므로 동일하게 처리
    const isIOSDevice =
      /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;

    // 추가 체크: 터치 지원 + Mac OS (iPad on macOS)
    const isTouchMac =
      /Macintosh/.test(ua) &&
      navigator.maxTouchPoints > 0 &&
      navigator.maxTouchPoints > 1;

    const detectedIOS = isIOSDevice || isTouchMac;

    setIsIOS(detectedIOS);
    // iOS는 WebM 알파채널 미지원이므로 HeroSection에서 fallback 사용
    setSupportsWebM(!detectedIOS);
  }, []);

  // iframe 환경 감지
  useEffect(() => {
    setIsInIframe(window.self !== window.top);
  }, []);

  // 페이지 마운트 시 초기화
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Video scroll control - 직접 호출 환경
  useEffect(() => {
    if (isInIframe || isIOS) return; // iframe이거나 iOS면 스크롤 제어 안 함

    const handleScroll = () => {
      if (
        heroVideoRef.current &&
        heroSectionRef.current &&
        videoDuration > 0 &&
        isVideoReady
      ) {
        const video = heroVideoRef.current;
        const sectionRect = heroSectionRef.current.getBoundingClientRect();
        const sectionCenter = sectionRect.top + sectionRect.height / 2;
        const viewportCenter = window.innerHeight / 2;

        const isAtCenter = Math.abs(sectionCenter - viewportCenter) < 300;
        const isPastCenter = sectionCenter < viewportCenter - 300;

        if (isAtCenter && videoPhase === "initial") {
          setVideoPhase("forward");
          video.currentTime = 0;
          video.play().catch(() => {});

          const stopAtMiddle = () => {
            if (video.currentTime >= videoDuration / 2) {
              video.pause();
              video.removeEventListener("timeupdate", stopAtMiddle);
              setVideoPhase("complete");
            }
          };
          video.addEventListener("timeupdate", stopAtMiddle);
        } else if ((isPastCenter || !isAtCenter) && videoPhase === "complete") {
          setVideoPhase("reverse");
          video.currentTime = videoDuration / 2;
          video.play().catch(() => {});

          const stopAtEnd = () => {
            if (video.currentTime >= videoDuration - 0.1) {
              video.pause();
              video.removeEventListener("timeupdate", stopAtEnd);
              setVideoPhase("initial");
            }
          };
          video.addEventListener("timeupdate", stopAtEnd);
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [videoDuration, videoPhase, isVideoReady, isInIframe, isIOS]);

  // iframe 환경에서 Intersection Observer로 비디오 자동 재생
  useEffect(() => {
    if (!isInIframe || !isVideoReady || iframePlayCount >= 1 || isIOS) return; // iOS면 제어 안 함

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            heroVideoRef.current &&
            videoDuration > 0
          ) {
            const video = heroVideoRef.current;

            if (videoPhase === "initial") {
              setVideoPhase("forward");
              video.currentTime = 0;
              video.play().catch(() => {});

              const playToHalf = () => {
                if (video.currentTime >= videoDuration / 2) {
                  video.pause();
                  video.removeEventListener("timeupdate", playToHalf);
                  setVideoPhase("complete");

                  setTimeout(() => {
                    setVideoPhase("reverse");
                    video.currentTime = videoDuration / 2;
                    video.play().catch(() => {});

                    const playToEnd = () => {
                      if (video.currentTime >= videoDuration - 0.1) {
                        video.pause();
                        video.removeEventListener("timeupdate", playToEnd);

                        const newCount = iframePlayCount + 1;
                        setIframePlayCount(newCount);

                        video.currentTime = 0;
                        setVideoPhase("initial");
                      }
                    };
                    video.addEventListener("timeupdate", playToEnd);
                  }, 2000);
                }
              };
              video.addEventListener("timeupdate", playToHalf);
            }
          }
        });
      },
      {
        threshold: 0.3,
      }
    );

    if (heroSectionRef.current) {
      observer.observe(heroSectionRef.current);
    }

    return () => {
      if (heroSectionRef.current) {
        observer.unobserve(heroSectionRef.current);
      }
    };
  }, [
    isInIframe,
    isVideoReady,
    videoDuration,
    videoPhase,
    iframePlayCount,
    isIOS,
  ]);

  return (
    <div className="landing-page overflow-x-hidden">
      <HeroSection />

      <VideoSection
        supportsWebM={supportsWebM}
        mainHeroVideo={mainHeroVideo}
        mainHeroFallback={mainHeroFallback}
        heroVideoRef={heroVideoRef}
        heroSectionRef={heroSectionRef}
        setVideoDuration={setVideoDuration}
        setIsVideoReady={setIsVideoReady}
        isIOS={isIOS}
        heroColorVideo={heroColorVideo}
        heroAlphaVideo={heroAlphaVideo}
        heroAlphaVideoRef={heroAlphaVideoRef}
      />

      <DesignSection />

      <TechSection
        techImage={techImage}
        easyUseImage={easyUseImage}
        laptopImages={laptopImages}
      />

      <ServicesSection laptopImages={laptopImages} clinicImage={clinicImage} />

      <KioskSection
        kioskVideo={isIOS ? kioskVideoWebP : kioskVideo}
        kioskFallback={kioskFallback}
        kioskContents={kioskContents}
        kioskVideoRef={kioskVideoRef}
        kioskSectionRef={kioskSectionRef}
        isIOS={isIOS}
        kioskColorVideo={kioskColorVideo}
        kioskAlphaVideo={kioskAlphaVideo}
      />

      <CTASection clinicImage={clinicImage} />
    </div>
  );
};

export default LandingPage;
